import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Shield, Loader2, AlertCircle, Sparkles, ChevronRight } from "lucide-react";
import auditLogo from "../assets/zentis-logo.png";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { setError("Email and password required"); return; }
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      const msg = err.response?.data?.detail || "Invalid credentials";
      setError(msg);
    }
    setLoading(false);
  };

  return (
    <div
      className="min-h-screen bg-background p-4"
      data-testid="login-page"
      style={{
        backgroundImage:
          "radial-gradient(circle at top left, rgba(59,130,246,0.18), transparent 26%), radial-gradient(circle at bottom right, rgba(96,165,250,0.28), transparent 28%), linear-gradient(135deg, #eff6ff 0%, #e0f2fe 100%)"
      }}
    >
      <div className="mx-auto flex min-h-screen w-full max-w-6xl items-center justify-center">
        <div className="login-shell grid w-full overflow-hidden rounded-[2rem] border border-[#c7d2fe] bg-white/75 shadow-[0_28px_80px_rgba(37,99,235,0.12)] backdrop-blur-xl lg:grid-cols-[1.08fr_0.92fr]">
          <div className="relative hidden overflow-hidden bg-[#171a22] px-10 py-12 text-white lg:block">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(59,130,246,0.28),transparent_26%),linear-gradient(180deg,#1b1e26_0%,#12141b_100%)]" />
            <div className="relative z-10 flex h-full flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-3 rounded-full bg-white/10 px-4 py-2 backdrop-blur">
                  <div className="flex h-11 w-11 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-[0_12px_30px_rgba(59,130,246,0.22)]">
                    <img src={auditLogo} alt="AuditOS logo" className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <p className="text-xl font-extrabold tracking-tight">AuditOS</p>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-blue-100/65">Internal Risk Intelligence</p>
                  </div>
                </div>
                <h1 className="mt-10 max-w-md text-5xl font-extrabold leading-[0.95] tracking-[-0.05em]">
                  Fast audits, sharper controls, cleaner execution.
                </h1>
                <p className="mt-5 max-w-lg text-sm leading-6 text-blue-50/78">
                  A modern command center for internal audit teams to move from document intake to final reporting with speed and clarity.
                </p>
              </div>

              <div className="grid gap-4">
                <div className="rounded-[1.5rem] border border-white/10 bg-white/5 p-5">
                  <div className="flex items-center gap-2 text-sm font-semibold text-blue-100">
                    <Sparkles className="h-4 w-4" />
                    Audit velocity snapshot
                  </div>
                  <div className="mt-5 grid grid-cols-3 gap-3 text-left">
                    <div className="rounded-2xl bg-white/6 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-blue-100/55">Coverage</p>
                      <p className="mt-2 text-2xl font-extrabold">87%</p>
                    </div>
                    <div className="rounded-2xl bg-white/6 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-blue-100/55">Findings</p>
                      <p className="mt-2 text-2xl font-extrabold">14</p>
                    </div>
                    <div className="rounded-2xl bg-white/6 p-3">
                      <p className="text-[11px] uppercase tracking-[0.18em] text-blue-100/55">Cycle Time</p>
                      <p className="mt-2 text-2xl font-extrabold">3.2d</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-blue-50/75">
                  <ChevronRight className="h-4 w-4 text-[#60a5fa]" />
                  Built for high-tempo audit operations and evidence-heavy workflows.
                </div>
              </div>
            </div>
          </div>
          <div className="login-panel flex items-center justify-center px-6 py-8 sm:px-10 sm:py-12">
            <div className="w-full max-w-md space-y-6">
              <div className="space-y-3 lg:hidden">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-[0_12px_30px_rgba(59,130,246,0.18)]">
                    <img src={auditLogo} alt="AuditOS logo" className="h-full w-full object-cover" />
                  </div>
                  <div>
                    <h1 className="login-title text-xl font-extrabold tracking-tight">AuditOS</h1>
                    <p className="text-[10px] uppercase tracking-[0.24em] text-muted-foreground">Internal Risk Intelligence</p>
                  </div>
                </div>
              </div>

              <div>
                <p className="text-[11px] font-extrabold uppercase tracking-[0.24em] text-[#2563eb]">Welcome Back</p>
                <h2 className="login-title mt-3 text-3xl font-extrabold tracking-[-0.04em] text-slate-900">Sign in to continue</h2>
                <p className="login-copy mt-2 text-sm text-slate-600">Use your audit credentials to access the audit control room.</p>
              </div>

              <Card className="login-card border-white/70 bg-white/88">
                <CardHeader className="pb-4">
                  <CardTitle className="text-base">Audit Access</CardTitle>
                  <CardDescription className="text-xs">Enter your credentials to access the audit system.</CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {error && (
                      <div className="flex items-center gap-2 rounded-2xl bg-destructive/10 px-3 py-3 text-sm text-destructive" data-testid="login-error">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        <span>{error}</span>
                      </div>
                    )}
                    <div>
                      <Label htmlFor="email" className="text-xs">Email</Label>
                      <Input
                        id="email" type="email" value={email}
                        onChange={e => setEmail(e.target.value)}
                        placeholder="admin@audit.com"
                        className="mt-1"
                        data-testid="login-email-input"
                        autoFocus
                      />
                    </div>
                    <div>
                      <Label htmlFor="password" className="text-xs">Password</Label>
                      <Input
                        id="password" type="password" value={password}
                        onChange={e => setPassword(e.target.value)}
                        placeholder="Enter password"
                        className="mt-1"
                        data-testid="login-password-input"
                      />
                    </div>
                    <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit-btn">
                      {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Shield className="mr-2 h-4 w-4" />}
                      Sign In
                    </Button>
                  </form>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
